<template>
  <v-dialog v-model="show" max-width="1000">
    <v-card>
      <v-card-title class="d-flex align-center">
        <div>{{ $tc('warning', 2) }}</div>
        <v-spacer/>
        <v-btn fab text small @click="show = false">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-list>
          <v-list-item v-for="(warning, index) in simulationWarnings" :key="index">
            <v-list-item-icon>
              <v-icon large color="warning">mdi-timer-alert</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="text-wrap">
                {{ warning.route_product.label }} - {{ $t('product_simulation_not_available') }}
              </v-list-item-title>
              <v-list-item-subtitle class="text-wrap">
                {{ $t('simulated_product_temperatures_not_available_after_90_minute_gap_1') }}
                <multi-time-display class="d-inline-block" :time="warning.start"/>
                <span v-html="$t('simulated_product_temperatures_not_available_after_90_minute_gap_2')"/>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item v-for="(warning, index) in generalWarnings" :key="index" three-line>
            <v-list-item-icon>
              <v-icon large color="warning">{{ warning.mdiIcon }}</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="text-wrap">
                <span v-if="warning.route_product">{{ warning.route_product.label }} - </span>
                {{ $t('warning_violation:' + warning.type) }}
              </v-list-item-title>
              <v-list-item-subtitle class="text-wrap">
                {{ warning.formattedMessage }}
              </v-list-item-subtitle>
              <v-list-item-subtitle v-if="warning.start !== warning.end">
                <multi-time-display :time="warning.start"/> -<multi-time-display :time="warning.end"/>
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item v-if="showCoolerSetPointWarning" three-line>
            <v-list-item-icon>
              <v-icon large color="warning">mdi-thermometer-alert</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="text-wrap">
                {{ $t('cooler_setpoint_warning') }}
              </v-list-item-title>
              <v-list-item-subtitle class="text-wrap">
                {{ $t('set_point_warning_message', { zone: $tc('cooler').toLowerCase() }) }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
          <v-list-item v-if="showFreezerSetPointWarning" three-line>
            <v-list-item-icon>
              <v-icon large color="warning">mdi-thermometer-alert</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title class="text-wrap">
                {{ $t('freezer_setpoint_warning') }}
              </v-list-item-title>
              <v-list-item-subtitle class="text-wrap">
                {{ $t('set_point_warning_message', { zone: $tc('freezer').toLowerCase() }) }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </v-list-item>
        </v-list>
      </v-card-text>
    </v-card>
    <template #activator="{on, attrs}">
      <div class="ml-5" v-if="numWarnings > 0">
        <vf-tooltip :text="$tc('warning', 2)">
          <v-badge :content="numWarnings" offset-x="13" offset-y="13" overlap color="warning darken-1" bordered>
            <v-btn small fab dark v-on="on" v-bind="attrs" color="warning" elevation="0">
              <v-icon size="30">
                mdi-exclamation-thick
              </v-icon>
            </v-btn>
          </v-badge>
        </vf-tooltip>
      </div>
    </template>
  </v-dialog>
</template>

<script>
import { each, get, size, filter, some } from 'lodash'
import MultiTimeDisplay from '@/components/shared/MultiTimeDisplay'
import VfTooltip from '@/components/shared/VfTooltip'
import RouteViolationRecord from '@/orm/models/RouteViolationRecord'

export default {
  name: 'RouteViolationsDialog',
  components: {
    MultiTimeDisplay,
    VfTooltip
  },
  props: {
    route: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      show: false
    }
  },
  computed: {
    numWarnings () {
      let result = size(this.simulationWarnings) + size(this.generalWarnings)
      if (this.showCoolerSetPointWarning) {
        result += 1
      }
      if (this.showFreezerSetPointWarning) {
        result += 1
      }
      return result
    },
    /**
     * Returns an object where each key is a route product id with the value being the first time-gap violation greater
     * than 90 minutes found for that product. May return an empty object.
     * @return Object
     */
    simulationWarnings () {
      const simulationWarnings = {}
      each(this.route.route_violations, (violation) => {
        violation = this.$orm('RouteViolation').createRecord(violation)
        // If it's the first 90 minute (or greater) time-gap record the violation
        if (violation.type === RouteViolationRecord.TYPE_GAP &&
          violation.duration >= 90 * 60 &&
          get(simulationWarnings, violation.route_product.id) == null) {
          simulationWarnings[violation.route_product.id] = violation
        }
      })
      return simulationWarnings
    },
    generalWarnings () {
      return filter(this.route.route_violations, x => x.level === RouteViolationRecord.LEVEL_WARNING)
    },
    showCoolerSetPointWarning () {
      return this.getSetPointWarning('cooler')
    },
    showFreezerSetPointWarning () {
      return this.getSetPointWarning('freezer')
    }
  },
  methods: {
    getSetPointWarning (zone) {
      const setPointHandle = zone + 'SetPoint'
      // Filter out all points where there's no set point data
      const setPointData = filter(this.route.interpolated_sensor_data, x => x[setPointHandle] != null)
      // Show the warning if there were no points where the ambient temp data was lower than the set point
      return some(setPointData, x => x[zone] <= x[setPointHandle])
    }
  }
}
</script>

<style scoped>
</style>
